import { FC } from 'react'
import styled from 'styled-components'
import { Text } from 'ui'
import { Step2Props } from './types'
import { EmeptyCalendarIcon } from '../icons'
import { earliestStartDate } from '../../../../store/couponSettings'

const FormField = styled.div`
  margin-bottom: 16px;
  width: 100%;
`

const Label = styled.div`
  margin-bottom: 8px;
  display: flex;
  justify-content: flex-start;
`

const DateInputContainer = styled.div`
  background-color: #FFFFFF;
  border-radius: 8px;
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  gap: 8px;
  padding-right: 16px;
  padding-left: 16px;
  min-height: 45px;
  width: 100%;
  box-sizing: border-box;
`

const StyledInput = styled.input`
  flex: 1;
  color: #272727;
  font-size: 14px;
  text-align: right;
  min-height: 45px;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;

  &::placeholder {
      color: #B0B0B0;
  }
  
  // Custom styling for date/time inputs
  &::-webkit-calendar-picker-indicator {
    display: none; // We used our own icon
  }
`

const Hint = styled.div`
  margin-top: 4px;
  display: flex;
  justify-content: flex-start;
`

// Every coupon runs 00:00 to 00:00, so the customer only picks days — there is no time input.
// Dates are held as local midnight; PublishCouponModal turns the chosen last day into the
// exclusive end boundary when it saves.
const toLocalMidnight = (date: Date): Date => {
    const d = new Date(date)
    d.setHours(0, 0, 0, 0)
    return d
}

const addDays = (date: Date, n: number): Date => {
    const d = toLocalMidnight(date)
    d.setDate(d.getDate() + n)
    return d
}

// Formats for <input type="date">, which expects YYYY-MM-DD in *local* time.
// toISOString() would shift the day for anyone east of UTC, so it is not used here.
const formatDateForInput = (date: Date | null): string => {
    if (!date) return ''
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const parseDateInput = (value: string): Date | null => {
    if (!value) return null
    const [year, month, day] = value.split('-').map(Number)
    if (!year || !month || !day) return null
    const d = new Date(year, month - 1, day, 0, 0, 0, 0)
    return Number.isNaN(d.getTime()) ? null : d
}

const Step2: FC<Step2Props> = ({
    startDate, setStartDate,
    endDate, setEndDate,
    minRangeDays,
}) => {

    // Never today: the coupon would have started at 00:00, which has already passed.
    const earliestStart = earliestStartDate()

    // The earliest last day that still satisfies the minimum duration.
    const minEndDate = startDate ? addDays(startDate, minRangeDays - 1) : earliestStart

    const handleStartChange = (value: string) => {
        const picked = parseDateInput(value)
        setStartDate(picked)

        // Keep the pair valid: a start date that no longer leaves room for the minimum
        // duration would otherwise leave a silently invalid range on screen.
        if (picked && endDate && endDate.getTime() < addDays(picked, minRangeDays - 1).getTime()) {
            setEndDate(addDays(picked, minRangeDays - 1))
        }
    }

    return (
        <>
            <FormField>
                <Label>
                    <Text align="right" size="sm" weight="normal" style={{ color: '#000' }}>
                        בתוקף מתאריך
                    </Text>
                </Label>
                <DateInputContainer onClick={() => (document.getElementById('start-date-input') as HTMLInputElement)?.showPicker()}>
                    <StyledInput
                        id="start-date-input"
                        type="date"
                        placeholder="עוד לא נבחר תאריך"
                        min={formatDateForInput(earliestStart)}
                        value={formatDateForInput(startDate)}
                        onChange={(e) => handleStartChange(e.target.value)}
                    />
                    <EmeptyCalendarIcon />
                </DateInputContainer>
                <Hint>
                    <Text align="right" size="sm" weight="normal" style={{ color: '#888' }}>
                        ההטבה מתחילה ב-00:00 של יום ההתחלה, ולכן אפשר להתחיל ממחר והלאה
                    </Text>
                </Hint>
            </FormField>

            <FormField>
                <Label>
                    <Text align="right" size="sm" weight="normal" style={{ color: '#000' }}>
                        עד תאריך
                    </Text>
                </Label>
                <DateInputContainer onClick={() => (document.getElementById('end-date-input') as HTMLInputElement)?.showPicker()}>
                    <StyledInput
                        id="end-date-input"
                        type="date"
                        placeholder="עוד לא נבחר תאריך"
                        min={formatDateForInput(minEndDate)}
                        disabled={!startDate}
                        value={formatDateForInput(endDate)}
                        onChange={(e) => setEndDate(parseDateInput(e.target.value))}
                    />
                    <EmeptyCalendarIcon />
                </DateInputContainer>
                <Hint>
                    <Text align="right" size="sm" weight="normal" style={{ color: '#888' }}>
                        {startDate
                            ? `היום האחרון של ההטבה, שמסתיימת ב-00:00 שאחריו. מינימום ${minRangeDays} ימים.`
                            : 'יש לבחור קודם תאריך התחלה'}
                    </Text>
                </Hint>
            </FormField>
        </>
    )
}

export default Step2
